/**
 * Funding & Capital clarity helpers — coverage tolerance, gap fill, ownership vs promote.
 */

/** Match backend decision_rules funding warning (±5% of Dev Cost = OK). */
export const FUNDING_COVERAGE_TOLERANCE = 0.05;

export function isFundingCoverageOk(ratio, tolerance = FUNDING_COVERAGE_TOLERANCE) {
  return Math.abs((Number(ratio) || 0) - 1) < tolerance;
}

/** Dev Cost − Total Funding (positive = shortfall). */
export function fundingGapChf(fundingTotal, totalDevCost) {
  return (Number(totalDevCost) || 0) - (Number(fundingTotal) || 0);
}

const FIELD_LABELS = {
  bankDebt: 'Bank',
  equityRequired: 'Equity (you)',
  privateCapital: 'Partner / Private',
};

/**
 * Overrides that bring Total Funding to Dev Cost.
 * Shortfall: add to Bank if Bank already &gt; 0; otherwise Equity (you) — never invent Bank from 0.
 * Surplus: reduce Bank → Equity → Partner until absorbed (may return multiple overrides).
 * @returns {{ fieldKey: string, override: { type: 'amount', value: number }, label: string, gap: number }[]}
 */
export function fillFundingGapOverrides({
  fundingTotal,
  totalDevCost,
  bankDebtAmount = 0,
  equityRequired = 0,
  privateCapitalAmount = 0,
}) {
  const gap = fundingGapChf(fundingTotal, totalDevCost);
  if (!Number.isFinite(gap) || Math.abs(gap) < 1) return [];

  let bank = Math.max(0, Number(bankDebtAmount) || 0);
  let equity = Math.max(0, Number(equityRequired) || 0);
  let partner = Math.max(0, Number(privateCapitalAmount) || 0);
  const out = [];

  if (gap > 0) {
    // Shortfall — do not invent Bank when unused
    if (bank > 0) {
      out.push({
        fieldKey: 'bankDebt',
        override: { type: 'amount', value: bank + gap },
        label: FIELD_LABELS.bankDebt,
        gap,
      });
    } else {
      out.push({
        fieldKey: 'equityRequired',
        override: { type: 'amount', value: equity + gap },
        label: FIELD_LABELS.equityRequired,
        gap,
      });
    }
    return out;
  }

  // Surplus — cascade reductions
  let surplus = -gap;
  const applyCut = (fieldKey, current) => {
    if (surplus <= 0 || current <= 0) return current;
    const cut = Math.min(current, surplus);
    const next = current - cut;
    surplus -= cut;
    out.push({
      fieldKey,
      override: { type: 'amount', value: next },
      label: FIELD_LABELS[fieldKey],
      gap,
    });
    return next;
  };

  bank = applyCut('bankDebt', bank);
  equity = applyCut('equityRequired', equity);
  partner = applyCut('privateCapital', partner);

  return out;
}

/** @deprecated Prefer fillFundingGapOverrides — kept for single-step callers/tests. */
export function fillFundingGapOverride(args) {
  const list = fillFundingGapOverrides(args);
  return list.length ? list[0] : null;
}

/** Sponsor vs partner principal split (funding only — not profit share). */
export function ownershipSplit(equityRequired, privateCapitalAmount) {
  const equity = Math.max(0, Number(equityRequired) || 0);
  const partner = Math.max(0, Number(privateCapitalAmount) || 0);
  const total = equity + partner;
  if (total <= 0) {
    return { sponsorPct: 0, partnerPct: 0, total: 0 };
  }
  return {
    sponsorPct: equity / total,
    partnerPct: partner / total,
    total,
  };
}

/** ROI denominator has no sponsor equity while partner capital is funding the deal. */
export function zeroEquityWithPartner({ equityRequired, privateCapitalAmount, pcIsEquity }) {
  return Boolean(pcIsEquity)
    && (Number(privateCapitalAmount) || 0) > 0
    && (Number(equityRequired) || 0) <= 0;
}
